import { MatDialog } from '@angular/material/dialog';
import { AfterViewInit, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DocumentsSectionData, FormDetailsActionType, FormDetailsComponentData } from '../../form-details.model';
import { getSubmissionForm, getThankYouDialogData } from '../../form-details.data';
import { FormDetailsBaseViewFacade } from './form-details-base-view.facade';
import { OwnerInfoDetailsService } from './services/owner-info-details.service';
import { TriggersService } from './services/triggers.service';
import { SharedDetailsService } from './services/shared-details.service';
import { FormDetailsActionsService } from '../../services/actions/form-details-actions.service';
import { AppBarAction, AppPageService, SimpleDialogComponent } from 'common';
import { AutosaveFormDetailsFacade } from '../../services/autosave/autosave-form-details.facade';
import { DocumentsDetailsService } from './services/documents-details.service';
import { finalize, tap } from 'rxjs/operators';
import { ApplicationData } from '../../../../../../domain/models/application.model';

@Component({
    selector: 'ifbp-form-details-base-view',
    templateUrl: './form-details-base-view.component.html',
    styleUrls: ['./form-details-base-view.component.scss'],
    providers: [FormDetailsBaseViewFacade, TriggersService, OwnerInfoDetailsService, SharedDetailsService, DocumentsDetailsService],
    standalone: false
})
export class FormDetailsBaseViewComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  initialData: FormDetailsComponentData;

  documentsData: DocumentsSectionData;
  submissionForm: FormGroup;
  actionInProgress: boolean;

  private abortController = new AbortController();

  constructor(
    private readonly facade: FormDetailsBaseViewFacade,
    private readonly autosaveFormDetailsFacade: AutosaveFormDetailsFacade,
    private readonly formDetailsActions: FormDetailsActionsService,
    private readonly dialog: MatDialog,
    private readonly appPageService: AppPageService,
  ) { }

  ngOnInit(): void {
    this.submissionForm = getSubmissionForm(this.initialData?.formData);
    this.documentsData = this.initialData?.documentsData;
    this.initActions(this.submissionForm, this.initialData?.application);
    this.facade.initAutosaveData(this.initialData);
  }

  ngAfterViewInit(): void {
    this.facade.initTriggers(this.submissionForm, this.abortController);
    this.facade.expandAllSectionsIfIncomplete(this.initialData?.application?.brokerStatus);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    this.facade.handleBeforeUnloadAction(event, this.submissionForm);
  }

  onAppBarActionInvoke(action: AppBarAction): void {
    if (this.actionInProgress) {
      return;
    }
    if (action.id !== FormDetailsActionType.SubmitForReview) {
      return;
    }

    this.submitForReview();
  }

  private initActions(submissionForm: FormGroup, application: ApplicationData): void {
    this.facade.initActions(submissionForm, application);
  }

  private submitForReview(): void {
    if (this.submissionForm.invalid || !this.documentsData.isValid) {
      this.submissionForm.markAllAsTouched();
      this.formDetailsActions.updateDocumentsData({ canMarkInvalidSection: true });
      this.formDetailsActions.markInvalidSections(true);
      return;
    }

    this.actionInProgress = true;
    this.autosaveFormDetailsFacade.submit().pipe(
      tap(() => this.handleSuccess()),
      finalize(() => this.handleComplete()))
      .subscribe();
  }

  private handleSuccess(): void {
    const dialogData = getThankYouDialogData(this.initialData.advisorData);
    SimpleDialogComponent.show(this.dialog, dialogData).subscribe(() => {
      this.appPageService.back();
    });
  }

  private handleComplete(): void {
    this.actionInProgress = false;
  }

  ngOnDestroy(): void {
    this.abortController.abort();
    this.autosaveFormDetailsFacade.clearAllData();
  }
}
